// Klartext-Erklärungen für Prüfregeln (Abnahme-Anforderung, 2026-07-04 Testrunde): Nutzer ohne
// Fachwissen (z. B. ein Handwerksbetrieb) sollen bei einer roten/gelben Meldung verstehen, WAS
// nicht stimmt und WIE man es beheben kann — ohne EN-16931-Jargon (Regel-IDs, "BT-xx"/"BG-xx"-Codes)
// entziffern zu müssen. Rein technische Erklärungen (§3.4): niemals Steuerbewertung, niemals
// Handlungsempfehlung zur Steuer, niemals "GoBD-konform" o. Ä. Die amtliche Formulierung bleibt
// unverändert erhalten und wird in der UI zusätzlich (eingeklappt) angezeigt, nie ersetzt.
//
// Feldnamen (BT-xx/BG-xx-Glossar) stammen aus der amtlichen Visualisierungs-Stylesheet-Lokalisierung
// (vendor/visualization/src/xsl/l10n/de.xml, Apache-2.0, D-002/LICENSES.md) — dieselben deutschen
// Feldbezeichnungen, die auch die offizielle Rechnungs-Vorschau verwendet.
//
// Abdeckung: alle 14 Regel-IDs, die im 114-Datei-Testkorpus + IBAN-Fixtures vorkommen (kuratiert,
// nicht automatisch generiert). Für alles andere gibt es einen ehrlichen generischen Fallback statt
// stillschweigend nichts zu erklären — siehe explainFinding().

/** Offizielle deutsche Feldbezeichnungen für die in unseren Regel-Erklärungen zitierten Codes. */
export const BT_BG_GLOSSARY: Record<string, string> = {
  'BT-72': 'Lieferdatum',
  'BT-73': 'Beginn Abrechnungszeitraum',
  'BT-74': 'Ende Abrechnungszeitraum',
  'BT-81': 'Code für das Zahlungsmittel',
  'BT-84': 'IBAN des Zahlungsempfängers',
  'BT-91': 'IBAN des Zahlungspflichtigen',
  'BT-112': 'Rechnungsgesamtsumme',
  'BT-113': 'bereits gezahlter Betrag',
  'BT-114': 'Rundungsbetrag',
  'BT-115': 'fälliger Betrag',
  'BG-14': 'Abrechnungszeitraum',
  'BG-17': 'Überweisung',
  'BG-19': 'Lastschrift',
  'BG-26': 'Abrechnungszeitraum je Rechnungsposition',
};

/** Kuratierte Klartext-Erklärung je Regel-ID. Rein technisch, keine Steuerbewertung (§3.4). */
export const RULE_EXPLANATIONS: Record<string, string> = {
  'BR-DE-TMP-32':
    'Es fehlt eine Angabe, wann geliefert bzw. die Leistung erbracht wurde. Ergänzen Sie in der ' +
    'Rechnung entweder ein Lieferdatum, einen Abrechnungszeitraum für die gesamte Rechnung, oder ' +
    'einen Abrechnungszeitraum bei jeder einzelnen Rechnungsposition.',
  'BR-DE-19':
    'Als Zahlungsart ist eine SEPA-Überweisung angegeben, aber die dazu angegebene IBAN ist ' +
    'ungültig — vermutlich ein Tippfehler oder ein fehlendes Zeichen. Prüfen Sie die Kontoverbindung ' +
    'auf der Rechnung.',
  'BR-DE-20':
    'Als Zahlungsart ist eine SEPA-Lastschrift angegeben, aber die IBAN des Zahlungspflichtigen ist ' +
    'ungültig — vermutlich ein Tippfehler oder ein fehlendes Zeichen. Prüfen Sie die Kontoverbindung ' +
    'auf der Rechnung.',
  'UBL-CR-646':
    'Diese Rechnung enthält eine technische XML-Unterstruktur bei einer Rechnungsposition, die im ' +
    'XRechnung-Format nicht vorgesehen ist. Das lässt sich meist nicht von Hand beheben — wenden Sie ' +
    'sich an den Hersteller der Software, mit der die Rechnung erstellt wurde.',
  'BR-DE-23-a':
    'Als Zahlungsart ist eine Überweisung angegeben, aber die dafür nötigen Angaben zur Überweisung ' +
    '(z. B. die Kontoverbindung) fehlen in der Rechnung. Ergänzen Sie die Überweisungsdaten.',
  'BR-CL-10':
    'Eine Kennung (z. B. eine Identifikationsnummer) verwendet ein Kennungs-Schema, das nicht in der ' +
    'amtlichen Liste (ISO 6523) enthalten ist. Prüfen Sie, mit welchem Schema die Kennung codiert ' +
    'wurde, und ob das richtige Schema-Kürzel verwendet wurde.',
  'BR-CL-13':
    'Die Klassifizierung eines Artikels bzw. einer Leistung verwendet ein Schema-Kürzel, das nicht ' +
    'in der amtlichen Liste (UNTDID 7143) enthalten ist. Prüfen Sie das verwendete Klassifizierungsschema.',
  'BR-CL-21':
    'Die Standard-Kennung eines Artikels verwendet ein Schema-Kürzel, das nicht in der amtlichen ' +
    'Liste (ISO 6523) enthalten ist. Prüfen Sie das verwendete Kennungsschema.',
  'BR-CO-16':
    'Der fällige Betrag stimmt rechnerisch nicht: Er muss genau der Rechnungsgesamtsumme entsprechen, ' +
    'abzüglich eines bereits gezahlten Betrags, zuzüglich eines Rundungsbetrags. Prüfen Sie die ' +
    'Beträge auf der Rechnung auf einen Rechenfehler.',
  'UBL-CR-470':
    'Diese Rechnung enthält eine Angabe zu einer Vorauszahlung, die im XRechnung-Format so nicht ' +
    'vorgesehen ist. Das lässt sich meist nicht von Hand beheben — wenden Sie sich an den Hersteller ' +
    'der Software, mit der die Rechnung erstellt wurde.',
  'BR-61':
    'Als Zahlungsart ist eine Überweisung angegeben, aber die Kontoverbindung (IBAN) des ' +
    'Zahlungsempfängers fehlt in der Rechnung. Ergänzen Sie die IBAN.',
  'PEPPOL-EN16931-R061':
    'Als Zahlungsart ist eine Lastschrift angegeben, aber die dafür nötige Mandatsreferenz fehlt in ' +
    'der Rechnung. Ergänzen Sie die Mandatsreferenz.',
  'BR-DE-25-a':
    'Als Zahlungsart ist eine Lastschrift angegeben, aber die dafür nötigen Angaben (z. B. ' +
    'Mandatsreferenz, Kontoverbindung) fehlen oder sind unvollständig. Ergänzen Sie die ' +
    'Lastschrift-Angaben.',
  'BR-DE-31':
    'Bei einer Lastschrift muss die Kontoverbindung (IBAN) des Zahlungspflichtigen angegeben sein. ' +
    'Diese fehlt in der Rechnung. Ergänzen Sie die IBAN.',
};

/** Generischer, ehrlicher Fallback für XML-Schema-Verstöße (cvc-*) — technische Software-Fehler,
 *  keine Handlungsempfehlung für Laien, da die Ursachen extrem vielfältig sein können. */
const XSD_FALLBACK =
  'Diese Datei entspricht nicht dem technischen XML-Format für XRechnung. Das ist in aller Regel ' +
  'ein Fehler in der Software, die die Rechnung erzeugt hat, und lässt sich nicht von Hand in der ' +
  'Rechnungsdatei reparieren. Die technische Meldung unten kann einer IT-Fachperson oder dem ' +
  'Rechnungsprogramm-Hersteller helfen, die Ursache zu finden.';

/** Ehrlicher Fallback für Regeln ohne eigene kuratierte Erklärung (noch nicht in RULE_EXPLANATIONS). */
const GENERIC_FALLBACK =
  'Für diese Prüfregel liegt noch keine ausführliche Klartext-Erklärung vor. Der amtliche Wortlaut ' +
  'unten beschreibt technisch, welche Angabe betroffen ist.';

/** Liefert die Klartext-Erklärung für ein Finding — nie leer, nie nur die Regel-ID. */
export function explainFinding(ruleId: string): string {
  if (RULE_EXPLANATIONS[ruleId]) return RULE_EXPLANATIONS[ruleId];
  if (ruleId.startsWith('cvc-') || ruleId === 'XSD') return XSD_FALLBACK;
  return GENERIC_FALLBACK;
}
